import type { BasePriceItem } from 'api-price'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import { CoinStyled, CurrencyStyled } from './style'
import { Flex } from '@mantine/core'
import { useAdapter } from '@/components/Adapter'
import BaseImage from '@/components/BaseImage'
import { COIN_ICON_URL, COIN_TRADE, HOME_PAGE_URL } from '@/constants/index'
import BaseTooltip from '@/components/BaseTooltip'
import useLangPrefix from '@/hooks/useLangPrefix'
import { coinImageUrl, fomartGateId, stopEvent } from '@/utils/tools'
import { useStore } from '@/store/index'
import dayjs from 'dayjs'
import { eventTracker } from '@/utils/collectEvent'

interface PriceCurrencyProps extends BasePriceItem {
  /** 是否显示收藏组件 */
  isCollect?: boolean
  onCollect: (item: BasePriceItem) => void
  isFundraising?: boolean
  project_id?: string
  project_name?: string
}

const Currency: FC<PriceCurrencyProps> = ({ isFundraising, isCollect = false, onCollect, ...item }) => {
  const { project_name, isFavorite, crypto, logo_url, currency, time, seo_path, fiat_name, gate_id } = item
  const { isH5 } = useAdapter()
  const { langPrefix } = useLangPrefix()
  const { priceTab } = useStore().baseStore

  const onClick = () => {
    eventTracker('price_treade_click', {
      button_name: 'card',
      gate_id: item?.gate_id,
      currency_name: item?.currency,
      crypto: item?.crypto,
    })

    const url =
      priceTab === HOME_PAGE_URL.FIAT
        ? `${langPrefix}/converter/${fomartGateId(
            gate_id,
            crypto
          )}/${crypto?.toLocaleLowerCase()}-to-${fiat_name?.toLocaleLowerCase()}`
        : `${langPrefix}/${seo_path ? 'price/' + seo_path : 'trade/' + crypto + '_' + COIN_TRADE}`

    location.href = url
  }

  if (isFundraising) {
    return (
      <a
        href={`${langPrefix}/price/fundraising/${item?.seo_path ?? (`${project_name}`?.replaceAll(' ', '-')?.replaceAll(/[?#<>&]/g, '') + '-' + item?.project_id).toLocaleLowerCase()}`}
      >
        <Flex gap={8} align="center">
          <BaseImage isRadius width={28} height={28} alt={project_name} src={logo_url} loading="eager" />

          <BaseTooltip label={project_name} className="text-1 size-16 font-500 line-120">
            {project_name}
          </BaseTooltip>
        </Flex>
      </a>
    )
  }

  return (
    <CurrencyStyled align="center" gap={{ base: 8, sm: 16 }}>
      {crypto === 'USDT' && isCollect ? (
        <div style={{ width: '18px' }}></div>
      ) : (
        <>
          {isCollect && (
            <svg
              onClick={e => {
                stopEvent(e)
                onCollect(item)
              }}
              className={`iconpark-icon size-20 ${isFavorite ? 'price-currency-favorite' : ''}`}
            >
              <use href={`${isFavorite === 1 ? '#v31-20px-fill-star' : '#v31-16px-line-star'}`}></use>
            </svg>
          )}
        </>
      )}

      <CoinStyled
        className="price-new-right"
        onClick={e => {
          stopEvent(e)
          onClick()
        }}
        data-new={String(Boolean(time && !dayjs().isAfter(dayjs(time * 1000))))}
        href={
          priceTab === HOME_PAGE_URL.FIAT
            ? `${langPrefix}/converter/${fomartGateId(
                gate_id,
                crypto
              )}/${crypto?.toLocaleLowerCase()}-to-${fiat_name?.toLocaleLowerCase()}`
            : `${langPrefix}/${seo_path ? 'price/' + seo_path : 'trade/' + crypto + '_' + COIN_TRADE}`
        }
      >
        <BaseImage
          isRadius
          width={isH5 ? 28 : 24}
          height={isH5 ? 28 : 24}
          alt={crypto}
          src={coinImageUrl(logo_url, crypto)}
          coin={crypto}
          loading="eager"
        />

        <Flex direction="column" gap={{ base: 2, sm: 4 }}>
          <BaseTooltip label={crypto} className="text-1 size-16 font-500 line-120 currency-price">
            {crypto}
            {priceTab === HOME_PAGE_URL.FIAT ? `/${fiat_name ?? ''}` : ''}
          </BaseTooltip>

          <BaseTooltip label={currency} className="text-3 size-14 font-400 line-120 currency-price">
            {currency}
          </BaseTooltip>
        </Flex>
      </CoinStyled>
    </CurrencyStyled>
  )
}

export default observer(Currency)
